/**
 * Non-component helpers shared by the CAD Record tab, its verification list, the apply dialog and
 * the assistant's CAD entry point.
 */
import { notificationService } from '../../services/notificationService';
import { CadPollState } from '../../hooks/useCadRecord';
import {
  CadApplyField,
  CadApplyResult,
  CadVerificationItem,
  CadVerificationResultName,
  CadVerificationTiming,
  CallAttachment,
  CallDetails,
} from '../../types/Call';

export const visuallyHidden = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const;

export const formatDateTime = (iso: string) =>
  new Date(iso).toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'short' });

/** Call record field names as the tab's toasts and the page's announcement read them out. */
export const APPLY_FIELD_LABELS: Record<CadApplyField, string> = {
  IncidentNumber: 'Incident number',
  CallType: 'Call type',
  CaseType: 'Case type',
  IncidentDate: 'Incident date',
  Location: 'Location',
  CallerName: 'Caller name',
  CallerNumber: 'Callback number',
  OperatorId: 'Operator alias',
  Operator: 'Operator name',
};

/** The distinct labels for a list of applied fields, in order, for a toast or an announcement. */
export const describeAppliedFields = (fields: CadApplyField[]): string =>
  [...new Set(fields.map((f) => APPLY_FIELD_LABELS[f] ?? f))].join(', ');

const RESULT_ORDER: CadVerificationResultName[] = [
  'Match',
  'Mismatch',
  'Missing',
  'Not on call',
  'Unclear',
];

/** "4 match, 1 mismatch, 2 not on call": counts in a fixed order, zero counts left out. */
export const tally = (items: CadVerificationItem[]) =>
  RESULT_ORDER.map((name) => ({ name, count: items.filter((i) => i.result === name).length }))
    .filter(({ count }) => count > 0)
    .map(({ name, count }) => `${count} ${name.toLowerCase()}`)
    .join(', ');

const formatSeconds = (seconds: number) => {
  const total = Math.round(seconds);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

/**
 * Timing as the check reported it: event log times copied as printed, and the intervals the model
 * computed. Nothing is derived in the browser; an interval the check did not compute is not listed.
 */
export const timingRows = (timing: CadVerificationTiming): { label: string; value: string }[] => {
  const rows: { label: string; value: string }[] = [];
  const printed = (label: string, value?: string | null) => {
    if (value?.trim()) rows.push({ label, value: value.trim() });
  };
  const seconds = (label: string, value?: number | null) => {
    if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
      rows.push({ label, value: formatSeconds(value) });
    }
  };

  printed('Call received', timing.callReceived);
  printed('First data entry', timing.firstDataEntry);
  printed('Nature entered', timing.natureEntered);
  printed('Call take finished', timing.callTakeFinished);
  printed('First dispatch', timing.firstDispatch);
  printed('First arrival', timing.firstArrival);
  seconds('Received to nature entered', timing.secondsReceivedToNatureEntered);
  seconds('Received to call take finished', timing.secondsReceivedToCallTakeFinished);
  seconds('Recording length', timing.recordingLengthSeconds);
  return rows;
};

/** Whether the call has scores a CAD Skills rescore can merge into: a completed analysis with a document. */
export const canRescoreCadSkills = (
  call: Pick<CallDetails, 'status' | 'detailedScoresJson'>
): boolean => call.status?.toLowerCase() === 'completed' && !!call.detailedScoresJson;

/** The rescore stage's wording, spelled once for the tab's banners, its live region and the assistant's line. */
export const RESCORE_WORDING = {
  queued: 'Queued to score CAD Skills against the CAD record',
  running: 'Scoring CAD Skills against the CAD record',
  failed: 'CAD Skills could not be rescored',
  landed: 'CAD Skills scored against the CAD record',
} as const;

/** "CAD Skills scored against the CAD record on 9/14/26, 4:00 PM", or without the date when the row has none. */
export const describeRescoreLanded = (record: Pick<CallAttachment, 'cadScoredAt'>): string =>
  `${RESCORE_WORDING.landed}${record.cadScoredAt ? ` on ${formatDateTime(record.cadScoredAt)}` : ''}`;

/**
 * One sentence about the rescore stage, for the tab's live region and the assistant's reply;
 * empty while nothing has been asked.
 */
export const describeRescore = (
  record: Pick<CallAttachment, 'rescoreStatus' | 'cadScoredAt'>,
  pollState: CadPollState
): string => {
  switch (record.rescoreStatus) {
    case 'NotRun':
      return '';
    case 'Pending':
    case 'Rescoring':
      if (pollState === 'timedOut') return 'Still scoring CAD Skills after 5 minutes.';
      if (pollState === 'unavailable') return 'The rescore status could not be read.';
      return `${record.rescoreStatus === 'Pending' ? RESCORE_WORDING.queued : RESCORE_WORDING.running}.`;
    case 'RescoreFailed':
      return `${RESCORE_WORDING.failed}.`;
    case 'Rescored':
      return `${describeRescoreLanded(record)}.`;
  }
};

/** Why the rescore is not offered yet; the tab and the assistant say it the same way. */
export const RESCORE_READINESS_NOTE =
  'CAD Skills can be rescored once the AI analysis of this call has completed.';

/** The request button, worded once for the tab and the assistant. */
export const rescoreButtonLabel = (record: Pick<CallAttachment, 'rescoreStatus'>): string =>
  record.rescoreStatus === 'Rescored' ? 'Update CAD Skills score again' : 'Update CAD Skills score';

/** The rescore confirmation, worded once for the tab and the assistant. */
export const RESCORE_DIALOG_TITLE = 'Update CAD Skills score?';
export const RESCORE_DIALOG_TEXT =
  'CallScore scores the CAD Skills category again using the CAD record, the transcript and the check against the call, then recomputes the overall score and the review status. Criteria with a manual score keep it, and a call marked Audited stays Audited. The change is recorded in the audit trail.';

/** Apply plan states as the dialog and the assistant's action bar both word them. */
export const APPLY_SETTINGS_LOADING = "Loading the agency's apply settings…";
export const APPLY_NOTHING_ALLOWED =
  'This agency does not allow filling call record fields from a CAD record.';

/** Mirrors A3.Logic CadApplyPlanner.UnchangedReason: a field already holding the CAD value. */
export const UNCHANGED_REASON = 'The call record already has this value.';

/**
 * Client-side pre-check for the obvious mistakes only. The size cap is the server's
 * Cad:MaxPdfSizeBytes, which is configuration, so it is not repeated here.
 */
export const validatePdf = (file: File): string | null => {
  const looksLikePdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
  if (!looksLikePdf) return 'Only PDF files can be attached as a CAD record.';
  if (file.size === 0) return 'The selected file is empty.';
  return null;
};

export interface CadApplyOutcome {
  /** Labels of the fields the apply wrote, in the order the server reported them. */
  appliedLabels: string[];
  /**
   * True when the call record now holds CAD values and must be re-read: fields were written, or
   * every requested field came back unchanged because a lost first response had already
   * committed them.
   */
  changed: boolean;
  /** True when the operator alias is now the CAD value, so losing access to the call is an expected outcome. */
  operatorChanged: boolean;
}

/**
 * Reports an apply's result through the notification service, the same way from the tab and from
 * the assistant, and says whether the call record must be re-read.
 */
export const reportApplyResult = (result: CadApplyResult): CadApplyOutcome => {
  const appliedLabels = result.applied.map((a) => APPLY_FIELD_LABELS[a.field] ?? a.field);
  const problems = result.skipped.filter((s) => s.reason !== UNCHANGED_REASON);
  const unchanged = result.skipped.filter((s) => s.reason === UNCHANGED_REASON);

  if (result.applied.length > 0) {
    notificationService.success(
      'CAD Record Applied',
      `Updated on the call record: ${appliedLabels.join(', ')}.`
    );
  } else if (problems.length === 0) {
    // Every requested field already held the CAD value, e.g. a repeated click after a save
    // whose response was lost. The call record is in the state the supervisor asked for.
    notificationService.success(
      'Call Record Up to Date',
      'The call record already has these values from the CAD record.'
    );
  }
  if (problems.length > 0) {
    notificationService.warning(
      'Some Fields Not Applied',
      problems.map((s) => `${APPLY_FIELD_LABELS[s.field] ?? s.field}: ${s.reason}`).join(' ')
    );
  }

  const changed = result.applied.length > 0 || unchanged.length > 0;
  return {
    appliedLabels,
    changed,
    operatorChanged:
      changed && [...result.applied, ...unchanged].some((f) => f.field === 'OperatorId'),
  };
};
